import React, { useMemo } from "react";
import { Pie, Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

export default function SurveyCharts({ allClauses }) {
  const { overallCompliant, overallNonCompliant, clauseLabels, clauseTitles, clausePercents } = useMemo(() => {
    let compliant = 0;
    let nonCompliant = 0;

    const labels = [];
    const titles = [];
    const percents = [];

    (allClauses || []).forEach((clause, idx) => {
      const qs = clause?.questions || [];

      let cCompliant = 0;
      let cNonCompliant = 0;

      qs.forEach((q) => {
        if (q.question_review_status === 'Accepted') cCompliant++;
        else if (q.question_review_status === 'Rejected') cNonCompliant++;
      });

      compliant += cCompliant;
      nonCompliant += cNonCompliant;

      const total = qs.length;
      const clausePercent = total > 0 ? Math.round((cCompliant / total) * 100) : 0;

      labels.push(clause.code || `C${idx + 1}`);
      titles.push(clause.title || clause.clause_title || clause.name || clause.code || `Clause ${idx + 1}`);
      percents.push(clausePercent);
    });

    return {
      overallCompliant: compliant,
      overallNonCompliant: nonCompliant,
      clauseLabels: labels,
      clauseTitles: titles,
      clausePercents: percents,
    };
  }, [allClauses]);

  const total = overallCompliant + overallNonCompliant;
  const compliantPct = total > 0 ? Math.round((overallCompliant / total) * 100) : 0;
  const nonCompliantPct = total > 0 ? 100 - compliantPct : 0;

  const pieData = {
    labels: ["Compliant (Pass)", "Non-Compliant (Fail)"],
    datasets: [
      {
        data: [overallCompliant, overallNonCompliant],
        backgroundColor: ["#14B8A6", "#EF4444"],
        borderColor: "#ffffff",
        borderWidth: 2,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: { enabled: true },
    },
  };

  const radarData = {
    labels: clauseLabels,
    datasets: [
      {
        label: "Compliance %",
        data: clausePercents,
        fill: true,
        backgroundColor: "rgba(37, 99, 235, 0.25)",
        borderColor: "#2563EB",
        pointBackgroundColor: "#2563EB",
        pointBorderColor: "#ffffff",
        pointHoverBackgroundColor: "#2563EB",
        pointHoverBorderColor: "#ffffff",
        borderWidth: 2,
        pointRadius: 3,
      },
    ],
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        suggestedMin: 0,
        suggestedMax: 100,
        grid: { color: "rgba(0,0,0,0.08)" },
        angleLines: { color: "rgba(0,0,0,0.08)" },
        pointLabels: { color: "#6B7280", font: { size: 11 } },
        ticks: { stepSize: 25, backdropColor: "transparent", color: "#9CA3AF" },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => clauseTitles[items[0]?.dataIndex] || clauseLabels[items[0]?.dataIndex] || '',
          label: (item) => `Compliance: ${item.raw}%`,
        },
      },
    },
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* LEFT: Pie */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
            <h3 className="text-sm font-semibold text-gray-900">Overall Compliance Distribution</h3>
          </div>
        </div>

        <div className="text-center text-sm text-gray-700 mb-2">
          <span className="font-medium">Compliant:</span> {compliantPct}% &nbsp;|&nbsp;
          <span className="font-medium">Non-Compliant:</span> {nonCompliantPct}%
        </div>

        <div className="h-[260px]">
          <Pie data={pieData} options={pieOptions} />
        </div>

        <div className="mt-3 flex items-center justify-center gap-6 text-xs text-gray-600 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-teal-500" />
            <span>Compliant (Pass): {overallCompliant}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-red-500" />
            <span>Non-Compliant (Fail): {overallNonCompliant}</span>
          </div>
        </div>
      </div>

      {/* RIGHT: Radar */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2.5 h-2.5 rounded-full bg-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-900">Clause-Based Compliance Analysis</h3>
        </div>

        <div className="h-[320px]">
          <Radar data={radarData} options={radarOptions} />
        </div>
      </div>
    </div>
  );
}
